import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  Image as ImageIcon,
  InsertDriveFile as GenericFileIcon,
  Close as CloseIcon,
} from '@material-ui/icons'
import { formatBytes } from 'utils/formatBytes'
import * as Styled from './ChatAttachmentItem.styles'

interface ChatAttachmentItemProps {
  name: string
  size?: number
  type?: string
  onRemove?: () => void
  disabled?: boolean
}

const getFileTypeDetails = (name: string, type?: string) => {
  let extension = ''
  if (name.includes('.')) {
    extension = name.split('.').pop()?.toUpperCase() || ''
  } else if (type) {
    extension = type.split('/')[1]?.toUpperCase() || ''
  }

  switch (extension) {
    case 'PDF':
      return { Icon: PdfIcon, tone: 'error' as const, label: extension }
    case 'DOC':
    case 'DOCX':
    case 'TXT':
      return { Icon: WordIcon, tone: 'secondary' as const, label: extension }
    case 'PNG':
    case 'JPG':
    case 'JPEG':
    case 'GIF':
    case 'SVG':
    case 'WEBP':
      return { Icon: ImageIcon, tone: 'primary' as const, label: extension }
    default:
      return { Icon: GenericFileIcon, tone: 'default' as const, label: extension }
  }
}

const ChatAttachmentItem: React.FC<ChatAttachmentItemProps> = ({
  name,
  size,
  type,
  onRemove,
  disabled,
}) => {
  const { t } = useTranslation()
  const { Icon, tone, label } = getFileTypeDetails(name, type)

  const metaParts = []
  metaParts.push(label || t('aiChat.file'))
  if (size !== undefined) metaParts.push(formatBytes(size, 1, t('aiChat.bytes')))

  const metaText = metaParts.join(' • ')

  return (
    <Styled.Wrapper>
      <Styled.IconBox $tone={tone}>
        <Icon fontSize='small' />
      </Styled.IconBox>
      <Styled.TextContainer>
        <Styled.FileName title={name}>{name}</Styled.FileName>
        {metaText && <Styled.FileMeta>{metaText}</Styled.FileMeta>}
      </Styled.TextContainer>
      {onRemove && (
        <Styled.CloseButton
          size='small'
          onClick={onRemove}
          disabled={disabled}
          aria-label={t('aiChat.removeAttachment', { name })}
        >
          <CloseIcon />
        </Styled.CloseButton>
      )}
    </Styled.Wrapper>
  )
}

export { ChatAttachmentItem }
